import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Card, Tab, Tabs } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

// custom hooks
import useISPowner from "../../hooks/useISPOwner";

// internal imports
import Loader from "../../components/common/Loader";
import { purchaseSms } from "../../features/apiCalls";
import ComponentCustomModal from "../../components/common/customModal/ComponentCustomModal";
import {
  getParchaseHistory,
  purchaseSMS,
} from "../../features/resellerParchaseSmsApi";

// Constants
const MIN_SMS_COUNT = 250;
const MIN_AMOUNT = 100;

const TAB = { TEXT: "text", VOICE: "voice" };
const MESSAGE_TYPE = {
  NON_MASKING: "nonMasking",
  MASKING: "masking",
  FIXED_NUMBER: "fixedNumber",
};
const BUY_FROM = { ONE_BILLING: "oneBilling", ISP_OWNER: "ispOwner" };

const DEFAULT_AMOUNT = { [TAB.TEXT]: 120, [TAB.VOICE]: 140 };

// message type -> rate field on the user object
const RATE_FIELD = {
  [MESSAGE_TYPE.NON_MASKING]: "smsRate",
  [MESSAGE_TYPE.MASKING]: "maskingSmsRate",
  [MESSAGE_TYPE.FIXED_NUMBER]: "fixedNumberSmsRate",
};

const getRate = (user, tab, messageType) =>
  tab === TAB.VOICE ? user?.voiceMessageRate : user?.[RATE_FIELD[messageType]];

const toCount = (amount, rate) => (rate ? Math.ceil(amount / rate) : 0);
const toAmount = (count, rate) => Math.ceil((rate || 0) * count);

// Small presentational component for a balance row
const BalanceItem = ({ label, balance }) => (
  <div>
    <label className="form-control-label">{label}</label>
    <span className={`text-${balance >= 0 ? "success" : "danger"} fw-bold`}>
      {balance}
    </span>
  </div>
);

const SMSPurchase = ({ show, setShow }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { role, bpSettings, ispOwnerData, userData } = useISPowner();

  const isReseller = role === "reseller";
  const user = role === "manager" ? ispOwnerData : userData;

  // reseller sms purchase history
  const purchaseHistory = useSelector(
    (state) => state?.smsHistory?.smsParchase,
  );
  const hasPendingInvoice = useMemo(
    () => (purchaseHistory || []).some((item) => item.status === "pending"),
    [purchaseHistory],
  );

  // ---- state ----
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB.TEXT);
  const [messageType, setMessageType] = useState(MESSAGE_TYPE.NON_MASKING);
  const [buyFrom, setBuyFrom] = useState(BUY_FROM.ONE_BILLING);
  const [amount, setAmount] = useState(DEFAULT_AMOUNT[TAB.TEXT]);
  const [count, setCount] = useState(() =>
    toCount(DEFAULT_AMOUNT[TAB.TEXT], user?.smsRate),
  );

  const isVoice = activeTab === TAB.VOICE;
  const rate = getRate(user, activeTab, messageType);

  useEffect(() => {
    if (isReseller) {
      getParchaseHistory(userData.id, dispatch, setIsLoading);
    }
  }, [isReseller, userData?.id, dispatch]);

  // switching tab resets message type & amount to that tab's defaults
  const handleTabChange = (tab) => {
    const defaultAmount = DEFAULT_AMOUNT[tab];
    const newRate = getRate(user, tab, MESSAGE_TYPE.NON_MASKING);

    setActiveTab(tab);
    setMessageType(MESSAGE_TYPE.NON_MASKING);
    setAmount(defaultAmount);
    setCount(toCount(defaultAmount, newRate));
  };

  // changing sms type keeps the count, recalculates the price
  const handleMessageTypeChange = (type) => {
    setMessageType(type);
    setAmount(toAmount(count, getRate(user, activeTab, type)));
  };

  const handleCountChange = (value) => {
    const newCount = Number(value) || 0;
    setCount(newCount);
    setAmount(toAmount(newCount, rate));
  };

  const handleAmountChange = (value) => {
    const newAmount = Math.ceil(Number(value) || 0);
    setAmount(newAmount);
    setCount(toCount(newAmount, rate));
  };

  const handleSubmit = () => {
    if (count < MIN_SMS_COUNT) {
      toast.error(t("unsuccessSMSalertPurchageModal"));
      return;
    }

    const buyFromOneBilling = !isReseller || buyFrom === BUY_FROM.ONE_BILLING;

    // purchase from one-billing
    if (buyFromOneBilling) {
      const payload = {
        amount: Math.ceil(amount),
        numberOfSms: count,
        user: user.user,
        type: isVoice ? "voiceMessagePurchase" : "smsPurchase",
        smsPurchaseType: messageType,
        ...(isReseller ? { reseller: userData.id } : { ispOwner: user.id }),
      };

      purchaseSms(payload, setIsLoading, dispatch, setShow, navigate);
      return;
    }

    // reseller purchasing from ISP owner
    if (hasPendingInvoice) {
      toast.error(t("statusCannotBePending"));
      return;
    }

    purchaseSMS(
      { smsAmount: count, smsParchaseType: messageType },
      setIsLoading,
      dispatch,
      setShow,
    );
  };

  return (
    <ComponentCustomModal
      show={show}
      setShow={setShow}
      header={t("smsPurchageBoard")}
      centered
      size="md"
      footer={
        <div className="displayGrid1 float-end">
          <button className="btn btn-secondary" onClick={() => setShow(false)}>
            {t("cancel")}
          </button>
          <button className="btn btn-success" onClick={handleSubmit}>
            {isLoading ? <Loader /> : t("buySMS")}
          </button>
        </div>
      }
    >
      <div className="container">
        <Tabs
          activeKey={activeTab}
          id="sms-purchase-tabs"
          className="mb-3"
          fill
          onSelect={handleTabChange}
        >
          <Tab eventKey={TAB.TEXT} title={t("text")}>
            <div className="displayGrid3 clintTitle p-2 mb-3">
              <BalanceItem label={t("nonMasking")} balance={user.smsBalance} />
              <BalanceItem
                label={t("masking")}
                balance={user.maskingSmsBalance}
              />
              <BalanceItem
                label={t("fixedNumber")}
                balance={user.fixedNumberSmsBalance}
              />
            </div>
          </Tab>

          {bpSettings?.voiceMessage && (
            <Tab eventKey={TAB.VOICE} title={t("voice")}>
              <div className="displayGrid3 clintTitle p-2 mb-3">
                <BalanceItem
                  label={t("nonMasking")}
                  balance={user?.voiceMessageBalance}
                />
              </div>
            </Tab>
          )}
        </Tabs>

        <Card className="bg-light">
          <Card.Body>
            <div className="monthlyBill text-center">
              <span className="fw-bold">{t("purchagePrice")} </span>
              <span className="price">
                <strong>৳{Math.ceil(amount)}</strong>
              </span>
            </div>

            <form className="displayGrid" onSubmit={(e) => e.preventDefault()}>
              <div>
                <label className="form-control-label text-secondary">
                  {t("smsType")}&nbsp;
                  <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select mw-100 mt-0 bg-white"
                  value={messageType}
                  onChange={(e) => handleMessageTypeChange(e.target.value)}
                  disabled={isVoice}
                >
                  <option value={MESSAGE_TYPE.NON_MASKING}>
                    {t("nonMasking")}
                  </option>
                  <option value={MESSAGE_TYPE.MASKING}>{t("masking")}</option>
                  <option value={MESSAGE_TYPE.FIXED_NUMBER}>
                    {t("fixedNumber")}
                  </option>
                </select>
              </div>

              <div>
                <label className="form-control-label text-secondary">
                  {`${t("SMS")} ${t("count")}`}&nbsp;
                  <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control bg-white"
                  type="number"
                  value={count}
                  min={MIN_SMS_COUNT}
                  onChange={(e) => handleCountChange(e.target.value)}
                />
              </div>

              <div>
                <label className="form-control-label text-secondary">
                  {t("amount")}&nbsp;
                  <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control bg-white"
                  type="number"
                  value={amount}
                  min={MIN_AMOUNT}
                  onChange={(e) => handleAmountChange(e.target.value)}
                />
              </div>

              {isReseller && (
                <div>
                  <label className="form-control-label text-secondary">
                    {t("selectPlace")}&nbsp;
                    <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select mw-100 mt-0 bg-white"
                    value={buyFrom}
                    onChange={(e) => setBuyFrom(e.target.value)}
                  >
                    <option value={BUY_FROM.ONE_BILLING}>One Billing</option>
                    <option value={BUY_FROM.ISP_OWNER}>Isp Owner</option>
                  </select>
                </div>
              )}
            </form>
          </Card.Body>
        </Card>
      </div>
    </ComponentCustomModal>
  );
};

export default SMSPurchase;
